import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiBriefcase, FiFileText, FiArrowRight, FiBarChart } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Header from '../components/Layout/Header';

const DashboardPage = () => {
  const navigate = useNavigate();

  const tools = [
    {
      id: 'job-posting',
      title: 'Job Post Creator',
      description: 'Generate professional job postings with AI assistance. Create compelling listings from job titles or existing descriptions.',
      icon: FiBriefcase,
      color: 'from-blue-500 to-blue-600',
      hoverColor: 'from-blue-600 to-blue-700',
      route: '/job-posting',
      features: ['AI-powered generation', 'Multiple export formats']
    },
    {
      id: 'meeting-minutes',
      title: 'Meeting Minutes Generator',
      description: 'Transform meeting transcripts into structured, professional minutes with action items and summaries.',
      icon: FiFileText,
      color: 'from-purple-500 to-purple-600',
      hoverColor: 'from-purple-600 to-purple-700',
      route: '/meeting-minutes',
      features: ['Smart transcription parsing', 'Action item extraction', 'Multiple formats']
    },
    {
      id: 'rfp-comparison',
      title: 'RFP Comparison Tool',
      description: 'Analyze and compare multiple Request For Proposals with detailed insights, comparative tables, and recommendations.',
      icon: FiBarChart,
      color: 'from-indigo-500 to-indigo-600',
      hoverColor: 'from-indigo-600 to-indigo-700',
      route: '/rfp-comparison',
      features: ['Multi-RFP analysis', 'Comparative insights', 'Decision framework']
    }
  ];

  const handleToolClick = (route) => {
    navigate(route);
  };

  const handleKeyPress = (event, route) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigate(route);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            AI Tools Dashboard
          </h1>
          <p className="text-gray-600">
            Choose a tool to streamline your workflow with AI assistance
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tools.map((tool, index) => (
            <motion.div
              key={tool.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group relative"
            >
              <div
                role="button"
                tabIndex={0}
                onClick={() => handleToolClick(tool.route)}
                onKeyDown={(e) => handleKeyPress(e, tool.route)}
                className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl hover:scale-105 focus:outline-none focus:ring-4 focus:ring-primary-500/20 focus:shadow-xl focus:scale-105"
              >
                {/* Header with gradient background */}
                <div className={`bg-gradient-to-r ${tool.color} group-hover:bg-gradient-to-r group-hover:${tool.hoverColor} p-6 transition-all duration-300`}>
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                      <SafeIcon icon={tool.icon} className="h-6 w-6 text-white" />
                    </div>
                    <SafeIcon 
                      icon={FiArrowRight} 
                      className="h-5 w-5 text-white/80 group-hover:text-white group-hover:translate-x-1 transition-all duration-300" 
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-white mt-4 mb-2">
                    {tool.title}
                  </h3>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {tool.description}
                  </p>

                  {/* Features list */}
                  <div className="space-y-2 mb-6">
                    {tool.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center text-sm text-gray-500">
                        <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></div>
                        {feature}
                      </div>
                    ))}
                  </div>

                  {/* Action button */}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full flex items-center justify-center py-3 px-4 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-lg font-medium transition-colors duration-200 group-hover:bg-primary-50 group-hover:text-primary-700"
                  >
                    <span>Open Tool</span>
                    <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;